import { FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { YachtForm } from "./yacht.schema";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { InputNumber } from "@/src/components/ui/input-number";
import { ScoreSelect } from "@/src/components/ui/score-select";
import { Card } from "@/src/components/wrappers/Card";
import { useFormContext } from "react-hook-form";
import { useState } from "react";
import { Input } from "@/src/components/ui/input";
import { cn } from "@/src/lib/utils";
import { PlusIcon, TrashIcon } from "lucide-react";
import { Button } from "@/src/components/ui/button";

const emptyExtraSpec = {
  name: "",
  value: "",
};

export function Specification() {
  const [extraSpec, setExtraSpec] = useState(emptyExtraSpec);
  const form = useFormContext<YachtForm>();
  const moreSpecs = form.watch("specification.extraSpecs");

  return (
    <>
      <Card>
        <div className="flex flex-col items-stretch">
          <h3 className="text-lg font-semibold mb-4">Specifications</h3>
          <div className="flex gap-12 items-stretch">
            <div className="flex flex-col flex-1 gap-6">
              <FormField<YachtForm, "specification.maxPeople">
                name="specification.maxPeople"
                render={({ field }) => (
                  <FormItem required className="flex space-y-0 items-center gap-4">
                    <FormLabel className="font-normal text-base">Guests</FormLabel>
                    <FormControl>
                      <ScoreSelect min={0} max={50} {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField<YachtForm, "specification.crewNumber">
                name="specification.crewNumber"
                render={({ field }) => (
                  <FormItem required className="flex space-y-0 items-center gap-4">
                    <FormLabel className="font-normal text-base">Crew</FormLabel>
                    <FormControl>
                      <ScoreSelect min={0} max={50} {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField<YachtForm, "specification.cabins">
                name="specification.cabins"
                render={({ field }) => (
                  <FormItem required className="flex space-y-0 items-center gap-4">
                    <FormLabel className="font-normal text-base">Cabins</FormLabel>
                    <FormControl>
                      <ScoreSelect min={0} max={50} {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField<YachtForm, "specification.crewCabins">
                name="specification.crewCabins"
                render={({ field }) => (
                  <FormItem required className="flex space-y-0 items-center gap-4">
                    <FormLabel className="font-normal text-base">Crew Cabins</FormLabel>
                    <FormControl>
                      <ScoreSelect min={0} max={50} {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField<YachtForm, "specification.berths">
                name="specification.berths"
                render={({ field }) => (
                  <FormItem required className="flex space-y-0 items-center gap-4">
                    <FormLabel className="font-normal text-base">Berths</FormLabel>
                    <FormControl>
                      <ScoreSelect min={0} max={50} {...field} disabled />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField<YachtForm, "specification.heads">
                name="specification.heads"
                render={({ field }) => (
                  <FormItem required className="flex space-y-0 items-center gap-4">
                    <FormLabel className="font-normal text-base">Heads</FormLabel>
                    <FormControl>
                      <ScoreSelect min={0} max={50} {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="w-0 border" />
            <div className="flex flex-col flex-1 gap-6">
              <FormField<YachtForm, "specification.length">
                name="specification.length"
                render={({ field }) => (
                  <FormItem required>
                    <FormLabel>Length</FormLabel>
                    <div className="flex items-center space-y-0 gap-4">
                      <FormControl>
                        <InputNumber {...field} />
                      </FormControl>
                      <span className="font-base">meters</span>
                    </div>
                  </FormItem>
                )}
              />
              <FormField<YachtForm, "specification.beam">
                name="specification.beam"
                render={({ field }) => (
                  <FormItem required>
                    <FormLabel>Beam</FormLabel>
                    <div className="flex items-center space-y-0 gap-4">
                      <FormControl>
                        <InputNumber {...field} />
                      </FormControl>
                      <span className="font-base">meters</span>
                    </div>
                  </FormItem>
                )}
              />
              <FormField<YachtForm, "specification.mainsailType">
                name="specification.mainsailType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mainsail</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="racing">Racing</SelectItem>
                        <SelectItem value="cruising">Cruising</SelectItem>
                        <SelectItem value="not_available">N/A</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
      </Card>
      <Card>
        <div className="flex flex-col items-stretch">
          <h3 className="text-lg font-semibold mb-4">More specs</h3>
          <div className="flex gap-6 items-stretch">
            <div className="flex flex-col flex-1 gap-6">
              <FormItem required>
                <FormLabel>Name</FormLabel>
                <Input
                  minLength={1}
                  className="max-w-[500px]"
                  value={extraSpec.name}
                  onChange={(ev) => {
                    const text = ev.target.value;
                    setExtraSpec({
                      ...extraSpec,
                      name: text,
                    });
                  }}
                />
              </FormItem>
              <FormItem required>
                <FormLabel>Value</FormLabel>
                <Input
                  minLength={1}
                  className={cn("max-w-[500px]")}
                  value={extraSpec.value}
                  onChange={(ev) => {
                    const text = ev.target.value;
                    setExtraSpec({
                      ...extraSpec,
                      value: text,
                    });
                  }}
                />
              </FormItem>
              <Button
                type="button"
                variant="secondary"
                disabled={!extraSpec.value || !extraSpec.name}
                onClick={() => {
                  if (extraSpec.value && extraSpec.name) {
                    form.setValue("specification.extraSpecs", [...moreSpecs, extraSpec]);
                  }
                  setExtraSpec(emptyExtraSpec);
                }}
              >
                Add to the yacht <PlusIcon size={16} />
              </Button>
            </div>
            <div className="w-0 border" />
            <div className="flex flex-col flex-1 max-w-80 gap-6">
              <h3 className="text-xl"> Specs added ({moreSpecs.length})</h3>
              {!moreSpecs.length && <p>No additional specs yet.</p>}
              {!!moreSpecs.length && (
                <div className="flex flex-col gap-4">
                  {moreSpecs.map((s, i) => (
                    <div
                      key={i}
                      className="flex flex-col p-4 border border-blue-100 bg-slate-50 rounded-sm items-stretch"
                    >
                      <div className="flex gap-2 items-center justify-between">
                        <h4 className="font-semibold">{s.name}</h4>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            form.setValue(
                              "specification.extraSpecs",
                              moreSpecs.filter((_, idx) => idx !== i),
                            );
                          }}
                        >
                          <TrashIcon size={16} />
                        </Button>
                      </div>
                      <span>{s.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    </>
  );
}
